/**
 * Cmarkdownmap Component
 * Renders a ```map fence (or a ```mermaid fence whose first line is `map`) as a
 * real map: a MapLibre canvas over OpenFreeMap vector tiles, with an ordered
 * location list that is always present beside it.
 *
 * The block is parsed by the SHARED parser (`@cabal/shared`), the same one the
 * back end reads, so the two can never disagree about which places a block
 * contains.
 *
 * Literal-coordinate pins carry their own position. A named place acquires one
 * from `useMapGeocoding`, which resolves the CONTENT RECORD this block sits in
 * — never the place strings — and only when a content ref is in scope. Outside
 * session content there is no ref, no request, and the block degrades to its
 * location list.
 */

'use client';

import { useCallback, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { parseMapBlock } from '@cabal/shared/business-logic/map/parse-map-block.js';
import type { DataElScope } from '@/utils/dataEl';
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from '../Cmarkdownvisual';
import { MapCompactPreview } from './MapCompactPreview';
import { useMapDefaultExpanded } from './map-expansion.context';
import { MapDroppedPinsNote } from './MapDroppedPinsNote';
import { MapSurface } from './MapSurface';
import {
  MAP_CONTENT_MIN_HEIGHT,
  MAP_METRIC_UNIT,
  MAP_SURFACE_IS_DARK,
} from './map-constants';
import {
  buildMapLocationEntries,
  hasResolvablePlaces,
  MAP_NO_SUPPRESSED_RESTATEMENTS,
  mapAccessibleName,
  mapCanvasPointsFromEntries,
  MapPinRestatement,
  MapRenderSurface,
  resolveMapRestatementChrome,
  selectMapRenderPlan,
  stripMapFenceHeader,
} from './map-render-model';
import { useMapGeocoding } from './useMapGeocoding';

export interface CmarkdownmapProps {
  dataElScope: DataElScope;
  content: string;
  className?: string;
  style?: CSSProperties;
  /**
   * Opens the block straight onto its drawn canvas instead of the collapsed
   * 317x317 tile. The tile is still reachable through the shell's collapse
   * control, so this is the block's STARTING surface, not a lock — and it is
   * the only way an embedder gets a real map, because `MapSurface` refuses to
   * mount `MapCanvas` while the shell is compact.
   *
   * Omitted, the block asks `CmarkdownmapExpansionProvider` — which is how an
   * embedder rendering real FENCES states the same intent, since the block
   * renderer passes content and nothing else. With neither, the tile.
   */
  defaultExpanded?: boolean;
  /**
   * Floor, in pixels, for the expanded body — canvas, list rail and note
   * together. Defaults to `MAP_CONTENT_MIN_HEIGHT`, which matches the
   * treemap/gantt canvases in a deliverable; an embedder that owns a smaller
   * slot sets its own.
   */
  contentMinHeight?: number;
  /**
   * Height, in pixels, for the drawn canvas. Reaches the canvas slot itself,
   * which `style` never does — `style` dresses the expanded root only — AND
   * the expanded stage, whose own floor lives in MapExpandedSurface.css and
   * would otherwise outrank it. Given, it governs; omitted, the stage keeps
   * its stylesheet default and the canvas slot `MAP_CANVAS_MIN_HEIGHT`.
   */
  canvasMinHeight?: number;
  /**
   * The restatements of a pin this embed does NOT want the block to draw,
   * because its own chrome already carries them. Empty by default: every block
   * names its pins in the rail and — for a coordinate pin — as a rounded
   * coordinate line, exactly as before. Markers themselves carry only their
   * glyph; a pin's name reaches the map through the aria-label and the click
   * popup, neither of which is suppressible.
   */
  suppressedRestatements?: readonly MapPinRestatement[];
}

export function Cmarkdownmap({
  dataElScope,
  content,
  className,
  style,
  defaultExpanded,
  contentMinHeight = MAP_CONTENT_MIN_HEIGHT,
  canvasMinHeight,
  suppressedRestatements = MAP_NO_SUPPRESSED_RESTATEMENTS,
}: CmarkdownmapProps): ReactElement {
  // Set once, by the canvas, when WebGL or the tile style gives out. The block
  // then keeps its list rather than vanishing.
  const [isCanvasAvailable, setIsCanvasAvailable] = useState(true);

  // The prop outranks the provider: an embedder holding this instance has said
  // something specific, while the provider is speaking to every fenced block
  // beneath it at once. `??` and not `||` — an explicit `false` is an answer.
  const embedderDefaultExpanded = useMapDefaultExpanded();
  const startsExpanded = defaultExpanded ?? embedderDefaultExpanded;

  const parsed = useMemo(() => parseMapBlock(stripMapFenceHeader(content)), [content]);
  const { phase, placeResolutions } = useMapGeocoding({
    hasPlaces: hasResolvablePlaces(parsed),
  });
  const entries = useMemo(
    () => buildMapLocationEntries({ pins: parsed?.pins ?? [], placeResolutions }),
    [parsed, placeResolutions],
  );
  const points = useMemo(() => mapCanvasPointsFromEntries(entries), [entries]);
  const contentStyle = useMemo<CSSProperties>(
    () => ({ minHeight: contentMinHeight, flex: 1 }),
    [contentMinHeight],
  );
  const restatementChrome = useMemo(
    () => resolveMapRestatementChrome({ suppressed: suppressedRestatements }),
    [suppressedRestatements],
  );
  const handleCanvasUnavailable = useCallback((): void => setIsCanvasAvailable(false), []);

  const plan = selectMapRenderPlan({
    parsed,
    positionedPointCount: points.length,
    resolutionPhase: phase,
    isCanvasAvailable,
  });

  // The ONLY error-card case. A block that parsed keeps its place names on
  // screen no matter what became of their positions — see MapSurface.
  if (!parsed || plan.surface === MapRenderSurface.ParseFailed) {
    return (
      <CmarkdownvisualError
        dataElScope="Cmarkdownmap/Visual"
        type={CmarkdownvisualType.Map}
        reason={CmarkdownvisualErrorReason.NoValidLocations}
      />
    );
  }

  // Undefined rather than an element that renders null: the shell paints a
  // separator whenever `controls` is present, so an empty note would leave a
  // stray divider in the top bar.
  const droppedPinsNote =
    parsed.droppedPinCount > 0 ? (
      <MapDroppedPinsNote dataElScope={dataElScope} droppedPinCount={parsed.droppedPinCount} />
    ) : undefined;

  return (
    <Cmarkdownvisual
      dataElScope="Cmarkdownmap/Visual"
      type={CmarkdownvisualType.Map}
      title={parsed.title}
      controls={droppedPinsNote}
      modalControls={droppedPinsNote}
      contentStyle={contentStyle}
      style={style}
      className={className}
      compactPreview={
        <MapCompactPreview
          dataElScope={dataElScope}
          entries={entries}
          showCoordinateDetail={restatementChrome.showCoordinateDetail}
        />
      }
      metric={{ value: entries.length, unit: MAP_METRIC_UNIT }}
      defaultExpanded={startsExpanded}
    >
      <MapSurface
        dataElScope={dataElScope}
        surface={plan.surface}
        note={plan.note}
        entries={entries}
        points={points}
        zoom={null}
        center={null}
        canvasMinHeight={canvasMinHeight ?? null}
        chrome={restatementChrome}
        isDark={MAP_SURFACE_IS_DARK}
        accessibleName={mapAccessibleName({
          title: parsed.title,
          positionedPointCount: points.length,
        })}
        onCanvasUnavailable={handleCanvasUnavailable}
      />
    </Cmarkdownvisual>
  );
}
